/**
 * Concatena los códigos de los niveles CIIU de forma secuencial hasta el nivel máximo disponible.
 * @param {Object} values - Objeto que contiene los niveles catalogciiu_levelX.
 * @returns {string} Cadena de códigos separados por punto y coma.
 */
export const concatCiiuLevelsHelper = (values) => {
  if (!values) return "";

  // 1. Buscamos cuál es el nivel máximo (del 6 al 1) que tiene datos
  let maxLevel = 0;
  for (let i = 6; i >= 1; i--) {
    const levelData = values[`catalogciiu_level${i}`];
    // Verifica si el nivel existe y tiene propiedades (Object.keys > 0)
    if (levelData && Object.keys(levelData).length > 0) {
      maxLevel = i;
      break; // Detiene la búsqueda al encontrar el nivel más alto poblado
    }
  }

  // 2. Extraemos los códigos secuencialmente únicamente hasta el nivel máximo encontrado
  const codes = [];
  for (let i = 1; i <= maxLevel; i++) {
    const code = values[`catalogciiu_level${i}`]?.code;
    if (code !== undefined && code !== null) {
      codes.push(code);
    }
  }

  // 3. Unimos todos los códigos con un punto y coma
  return codes.join(";");
};


/**
 * Concatena los códigos de los niveles de sección secuencialmente hasta el nivel máximo disponible.
 * @param {Object} values - Objeto que contiene los niveles catalogsection_levelX.
 * @returns {string} Cadena de códigos separados por punto y coma.
 */
export const concatSectionLevelsHelper = (values) => {
  if (!values) return "";

  // 1. Encontrar el nivel máximo poblado (del 3 al 1)
  let maxLevel = 0;
  for (let i = 3; i >= 1; i--) {
    const levelData = values[`catalogsection_level${i}`];
    if (levelData && Object.keys(levelData).length > 0) {
      maxLevel = i;
      break; // Detiene el bucle al identificar el nivel más alto con datos
    }
  }

  // 2. Extraer los códigos de manera secuencial hasta el nivel máximo hallado
  const codes = [];
  for (let i = 1; i <= maxLevel; i++) {
    const code = values[`catalogsection_level${i}`]?.code;
    if (code !== undefined && code !== null) {
      codes.push(code);
    }
  }

  // 3. Unificar el arreglo utilizando el separador de punto y coma
  return codes.join(";");
};


 /**
 * Concatena los códigos de los niveles de fases secuencialmente hasta el nivel máximo disponible.
 * @param {Object} values - Objeto que contiene los niveles catalogphase_levelX.
 * @returns {string} Cadena de códigos separados por punto y coma.
 */
export const concatPhaseLevelsHelper = (values) => {
  if (!values) return "";

  // 1. Encontrar el nivel máximo poblado (del 5 al 1)
  let maxLevel = 0;
  for (let i = 5; i >= 1; i--) {
    const levelData = values[`catalogphase_level${i}`];
    if (levelData && Object.keys(levelData).length > 0) {
      maxLevel = i;
      break; // Se detiene al identificar el nivel más alto con datos
    }
  }

  // 2. Extraer los códigos de manera secuencial hasta el nivel máximo hallado
  const codes = [];
  for (let i = 1; i <= maxLevel; i++) {
    const code = values[`catalogphase_level${i}`]?.code;
    if (code !== undefined && code !== null) {
      codes.push(code);
    }
  }

  // 3. Unificar el arreglo utilizando el separador de punto y coma
  return codes.join(";");
};


/**
 * Función genérica para concatenar códigos de catálogos multinivel de forma secuencial.
 * @param {Object} values - Objeto con los datos del formulario.
 * @param {string} prefix - Prefijo del catálogo (ej: "catalogciiu", "catalogsection", "catalogphase").
 * @param {number} maxLevels - Cantidad máxima de niveles que tiene el catálogo (ej: 6, 3, 5).
 * @returns {string} Cadena de códigos unidos por ";".
 */
export const concatDynamicLevelsHelper = (values, prefix, maxLevels) => {
  if (!values || !prefix || !maxLevels) return "";

  let maxLevelFound = 0;
  for (let i = maxLevels; i >= 1; i--) {
    const levelData = values[`${prefix}_level${i}`];
    if (levelData && Object.keys(levelData).length > 0) {
      maxLevelFound = i;
      break;
    }
  }

  const codes = [];
  for (let i = 1; i <= maxLevelFound; i++) {
    const code = values[`${prefix}_level${i}`]?.code;
    if (code !== undefined && code !== null) {
      codes.push(code);
    }
  }

  return codes.join(";");
};

const MULTILEVEL_GROUPS = [
  { prefix: "catalogciiu", idField: "catalogciius_id", levelsField: "catalogciiu_levels", max: 6 },
  { prefix: "catalogsection", idField: "catalogsections_id", levelsField: "catalogsection_levels", max: 3 },
  { prefix: "catalogphase", idField: "catalogphases_id", levelsField: "catalogphase_levels", max: 5 }
];

export const areMultilevelSelectsHydrated = (values, prefix) => {
  const level1 = values?.[`${prefix}_level1`];
  return Boolean(level1 && typeof level1 === "object" && level1.id);
};

const isMultilevelComboObject = (value) => (
  Boolean(value) && typeof value === "object" && value.id
);

/**
 * El GET/PUT no trae catalog*_levelN (son solo de UI). Al mezclar el
 * registro plano no se deben perder los objetos de combo ya hidratados.
 */
export const mergePreservingMultilevelSelects = (prev, incoming) => {
  const merged = { ...(prev || {}), ...(incoming || {}) };
  MULTILEVEL_GROUPS.forEach(({ prefix, max }) => {
    for (let i = 1; i <= max; i++) {
      const key = `${prefix}_level${i}`;
      if (!isMultilevelComboObject(merged[key]) && isMultilevelComboObject(prev?.[key])) {
        merged[key] = prev[key];
      }
    }
  });
  return merged;
};

/**
 * Los combos muestran catalog*_levelN (objetos). La validación y el API
 * usan el id hoja (número) y el string de códigos "A;A01;...".
 */
export const syncMultilevelLeafValues = (values) => {
  if (!values || typeof values !== "object") {
    return values;
  }

  MULTILEVEL_GROUPS.forEach(({ prefix, idField, levelsField, max }) => {
    let lastId = null;
    for (let i = max; i >= 1; i--) {
      const levelData = values[`${prefix}_level${i}`];
      if (levelData && typeof levelData === "object" && levelData.id) {
        lastId = levelData.id;
        break;
      }
    }
    const levels = concatDynamicLevelsHelper(values, prefix, max);
    if (lastId != null && lastId !== "") {
      values[idField] = lastId;
    }
    if (levels) {
      values[levelsField] = levels;
    }
  });

  return values;
};
